import { css } from '@emotion/react';
import React from 'react';

import { LAYOUT2 } from '@/Client/Styles';
import { styleguideColors as COLORS, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

const FOCUSED_LINE_ITEM_STRIP = css`
  label: FOCUSED_LINE_ITEM_STRIP;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  padding: ${LAYOUT2.spacing1}px 12px;
  background: ${COLORS.paper};
`;

/** Layered onto FOCUSED_LINE_ITEM_STRIP under a blocking flag: washed in red. */
const FOCUSED_LINE_ITEM_STRIP_ERROR = css`
  label: FOCUSED_LINE_ITEM_STRIP_ERROR;
  background: ${COLORS.errorFill};
`;

const FOCUSED_LINE_ITEM_STRIP_MESSAGE = css`
  label: FOCUSED_LINE_ITEM_STRIP_MESSAGE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12)};
  color: ${COLORS.ink};

  & b {
    font-weight: ${FONT_WEIGHT.bold};
    font-variant-numeric: tabular-nums;
  }

  & s {
    color: ${COLORS.midtone};
  }

  & em {
    font-style: normal;
    color: ${COLORS.midtone};
  }
`;

const FOCUSED_LINE_ITEM_STRIP_ACTIONS = css`
  label: FOCUSED_LINE_ITEM_STRIP_ACTIONS;
  margin-left: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FOCUSED_LINE_ITEM_STRIP_BUTTON = css`
  label: FOCUSED_LINE_ITEM_STRIP_BUTTON;
  padding: 6px 13px;
  border: 0;
  background: ${COLORS.ink};
  color: ${COLORS.paper};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9.5)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
`;

/** Layered onto FOCUSED_LINE_ITEM_STRIP_BUTTON for a secondary action: outlined rather than filled. */
const FOCUSED_LINE_ITEM_STRIP_BUTTON_GHOST = css`
  label: FOCUSED_LINE_ITEM_STRIP_BUTTON_GHOST;
  background: transparent;
  color: ${COLORS.ink};
  border: 1px solid ${COLORS.midtone};

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/**
 * A strip under a line item row, for something on the line to see to: its tag (a FocusedLineItemTab) and what it says
 * (FocusedLineItemStripMessage), then its actions at its end.
 */
export const FocusedLineItemStrip = ({ testId, isError, actions, children }: {
  testId: string;
  /** Washes the strip red, for a flag that blocks the line. */
  isError?: boolean;
  /** Its FocusedLineItemStripButtons. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div css={[FOCUSED_LINE_ITEM_STRIP, isError && FOCUSED_LINE_ITEM_STRIP_ERROR]} data-testid={testId}>
    {children}
    {actions && <span css={FOCUSED_LINE_ITEM_STRIP_ACTIONS}>{actions}</span>}
  </div>
);

/** A run of a strip's text: amounts in bold, what they were struck through, asides in emphasis. */
export const FocusedLineItemStripMessage = ({ testId, children }: { testId?: string; children: React.ReactNode }) => (
  <span css={FOCUSED_LINE_ITEM_STRIP_MESSAGE} data-testid={testId}>{children}</span>
);

/** One of a strip's actions: filled, or outlined when it's secondary. */
export const FocusedLineItemStripButton = ({ isSecondary, disabled, onClick, children }: {
  isSecondary?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) => (
  <button css={[FOCUSED_LINE_ITEM_STRIP_BUTTON, isSecondary && FOCUSED_LINE_ITEM_STRIP_BUTTON_GHOST]} type='button' disabled={disabled} onClick={onClick}>
    {children}
  </button>
);
